import { ActiveGpsSession, GpsSession, TaxiAppSettings, TaxiLog } from '@/types/taxi';

const STORAGE_KEYS = {
	logs: 'taxi-ai.logs.v1',
	gpsSessions: 'taxi-ai.gps-sessions.v1',
	activeGpsSession: 'taxi-ai.active-gps-session.v1',
	settings: 'taxi-ai.settings.v1',
};

const DEFAULT_SETTINGS: TaxiAppSettings = {
	claudeApiKey: '',
	spreadsheetWebhookUrl: '',
	defaultAreaHint: '新宿駅東口',
};

const safeParse = <T>(value: string | null, fallback: T): T => {
	if (!value) return fallback;
	try {
		return JSON.parse(value) as T;
	} catch (error) {
		console.error('Failed to parse storage value:', error);
		return fallback;
	}
};

const write = <T>(key: string, value: T) => {
	localStorage.setItem(key, JSON.stringify(value));
};

export const getTaxiLogs = () =>
	safeParse<TaxiLog[]>(localStorage.getItem(STORAGE_KEYS.logs), []);

export const saveTaxiLog = (log: TaxiLog) => {
	const logs = getTaxiLogs();
	write(STORAGE_KEYS.logs, [log, ...logs]);
};

export const removeTaxiLogById = (id: string) => {
	const nextLogs = getTaxiLogs().filter((log) => log.id !== id);
	write(STORAGE_KEYS.logs, nextLogs);
};

export const markLogsSynced = (ids: string[]) => {
	if (ids.length === 0) return;
	const idSet = new Set(ids);
	const next = getTaxiLogs().map((log) =>
		idSet.has(log.id) ? { ...log, synced: true } : log,
	);
	write(STORAGE_KEYS.logs, next);
};

export const getGpsSessions = () =>
	safeParse<GpsSession[]>(localStorage.getItem(STORAGE_KEYS.gpsSessions), []);

export const saveGpsSession = (session: GpsSession) => {
	const sessions = getGpsSessions();
	write(STORAGE_KEYS.gpsSessions, [session, ...sessions]);
};

export const markGpsSessionsSynced = (ids: string[]) => {
	if (ids.length === 0) return;
	const idSet = new Set(ids);
	const next = getGpsSessions().map((session) =>
		idSet.has(session.id) ? { ...session, synced: true } : session,
	);
	write(STORAGE_KEYS.gpsSessions, next);
};

export const getActiveGpsSession = () =>
	safeParse<ActiveGpsSession | null>(
		localStorage.getItem(STORAGE_KEYS.activeGpsSession),
		null,
	);

export const saveActiveGpsSession = (session: ActiveGpsSession) => {
	write(STORAGE_KEYS.activeGpsSession, session);
};

export const clearActiveGpsSession = () => {
	localStorage.removeItem(STORAGE_KEYS.activeGpsSession);
};

export const getTaxiSettings = () =>
	({
		...DEFAULT_SETTINGS,
		...safeParse<TaxiAppSettings>(localStorage.getItem(STORAGE_KEYS.settings), {
			claudeApiKey: '',
			spreadsheetWebhookUrl: '',
			defaultAreaHint: DEFAULT_SETTINGS.defaultAreaHint,
		}),
	}) as TaxiAppSettings;

export const saveTaxiSettings = (settings: TaxiAppSettings) => {
	write(STORAGE_KEYS.settings, settings);
};

export const getUnsyncedData = () => {
	const logs = getTaxiLogs().filter((log) => !log.synced);
	const gpsSessions = getGpsSessions().filter((session) => !session.synced);
	return { logs, gpsSessions };
};
